import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartLineItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  selectedAttributes?: Record<string, string>;
}

interface CartState {
  itemCount: number;
  items: CartLineItem[];
}

const getInitialCartItems = (): CartLineItem[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const persistedCart = window.localStorage.getItem(CART_STORAGE_KEY);

  if (!persistedCart) {
    return [];
  }

  try {
    const parsedCart = JSON.parse(persistedCart);
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch {
    return [];
  }
};

const persistCartItems = (items: CartLineItem[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

const clearPersistedCartItems = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(CART_STORAGE_KEY);
};

export const CART_STORAGE_KEY = 'cart';

const initialCartItems = getInitialCartItems();

const initialState: CartState = {
  itemCount: initialCartItems.reduce((count, item) => count + item.quantity, 0),
  items: initialCartItems,
};

const upsertItem = (items: CartLineItem[], payload: CartLineItem): CartLineItem[] => {
  const existingItem = items.find((item) => item.id === payload.id);

  if (!existingItem) {
    return [...items, payload];
  }

  return items.map((item) =>
    item.id === payload.id
      ? {
          ...item,
          quantity: item.quantity + payload.quantity,
          name: payload.name || item.name,
          price: payload.price || item.price,
          image: payload.image || item.image,
          selectedAttributes: payload.selectedAttributes || item.selectedAttributes,
        }
      : item
  );
};

const recalculateItemCount = (items: CartLineItem[]): number =>
  items.reduce((count, item) => count + item.quantity, 0);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartItems(state, action: PayloadAction<CartLineItem[]>) {
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.itemCount = recalculateItemCount(state.items);
      persistCartItems(state.items);
    },
    addItemToCart(state, action: PayloadAction<CartLineItem>) {
      state.items = upsertItem(state.items, action.payload);
      state.itemCount = recalculateItemCount(state.items);
      persistCartItems(state.items);
    },
    removeItemFromCart(state, action: PayloadAction<number>) {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.itemCount = recalculateItemCount(state.items);
      persistCartItems(state.items);
    },
    updateQuantity(state, action: PayloadAction<{ id: number; quantity: number }>) {
      const { id, quantity } = action.payload;

      state.items = state.items
        .map((item) =>
          item.id === id
            ? {
                ...item,
                quantity,
              }
            : item
        )
        .filter((item) => item.quantity > 0);

      state.itemCount = recalculateItemCount(state.items);
      persistCartItems(state.items);
    },
    clearCart(state) {
      state.itemCount = 0;
      state.items = [];
      clearPersistedCartItems();
    },
  },
});

export const { setCartItems, addItemToCart, removeItemFromCart, updateQuantity, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
